import { useConfig } from '#/hooks/useConfig';
import { tb } from '#/store/translation/translation.store';

export enum UserAccessErrorReason {
  LOCATION = 'location',
  AUTHENTICATION = 'authentication',
  ENTITLEMENT = 'entitlement',
  DEVICE_CONCURRENCY = 'device-concurrency',
  NOT_PLAYABLE = 'currently-not-playable',
  PARENTAL_LOCK = 'parental-lock',
}

export enum AuthorizationErrorReason {
  MISSING_TOKEN = 'missing-token',
  INVALID_TOKEN = 'invalid-token',
}

export class GeoRestrictionError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = tb('app.geoRestriction.title');
    this.message = tb('app.geoRestriction.message');
  }
}

export class PlaybackError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = tb('error.generic.title');

    if (!this.message) {
      this.message = tb('error.playbackStream.message');
    }
  }
}

/** A wrapper around Error with more details about failed response. It is enriched on the error interceptor level */
export class ApiError extends Error {
  details: any;
  statusCode: number;
  constructor(message?: string, details?: any) {
    super(message);
    this.details = details;
    this.statusCode = details?.response?.status ?? 400;
  }
}

export class UserAccessError extends Error {
  static readonly key: string = 'x-forbidden-reason';
  reason: UserAccessErrorReason;

  private readonly getTranslations = (reason: UserAccessErrorReason) => {
    const getPair = (keyPrefix: string) => ({
      name: tb(`${keyPrefix}.title`),
      message: tb(`${keyPrefix}.message`),
    });

    switch (reason) {
      case UserAccessErrorReason.AUTHENTICATION:
        return getPair('error.authenticationRequired');
      case UserAccessErrorReason.ENTITLEMENT:
        return getPair(
          useConfig().featureConfig.purchase?.inApp?.enabled
            ? 'error.entitlementRequired'
            : 'error.entitlementRequiredNoPurchase'
        );
      case UserAccessErrorReason.DEVICE_CONCURRENCY:
        return getPair('error.playbackConcurrency');
      case UserAccessErrorReason.LOCATION:
        return getPair('app.geoRestriction');
      case UserAccessErrorReason.NOT_PLAYABLE:
        return getPair('error.playbackCurrentlyNotAvailable');
      default:
        return {
          name: tb('web.app.accessDenied'),
          message: reason,
        };
    }
  };

  constructor(message: string, reason: UserAccessErrorReason) {
    super(message);
    this.reason = reason;
    const translations = this.getTranslations(reason);
    this.name = translations.name;
    this.message = translations.message;
  }
}

export class AuthorizationError extends Error {
  static readonly reason: string = 'x-authorization-error';
  reason: AuthorizationErrorReason;

  constructor(message: string, reason: AuthorizationErrorReason) {
    super(message);
    this.reason = reason;
  }
}

/** Determines whether the response indicates user access denial. */
export const isUserAccessResponse = (res: Response) => {
  const reason = res.headers?.get(UserAccessError.key);
  const isValidReason =
    reason && Object.values(UserAccessErrorReason).includes(reason);

  return (res.status === 403 && isValidReason) || false;
};

/** Determines whether the cause of an error was user access denial. */
export const isUserAccessError = (
  error?: Error,
  withReason?: UserAccessErrorReason
) => {
  return (
    error instanceof UserAccessError &&
    (!withReason || error.reason === withReason)
  );
};

/** Determines whether the cause of an error was geo-restriction. */
export const isGeoRestrictionError = (error?: unknown) => {
  return error instanceof GeoRestrictionError;
};

export const isPlaybackError = (error?: unknown) => {
  return error instanceof PlaybackError;
};
